import { Dayjs } from "dayjs";
import {
  FC,
  KeyboardEvent,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import { StationTimeEditFieldStyled } from "@ryde/controls";
import { CourseStationContext, useCourseDtoActions } from "@ryde/redux";
import {
  courseHasUnsavedStationChangeAtom,
  KeyboardKeys,
  Log,
  MAIN_TIME_FORMAT,
  parseTimeAtReferenceDate,
  SINGLE_KEY,
  TIME_STRING_LENGTH_HHmm,
  TimeZoneEnum,
  TTimeHHmm,
  updateCourseMetadataStationsTime,
  useFormats,
} from "@ryde/shared";

const UPDATED_STATION_TIME_VALIDATION_ERROR_LIFETIME = 10_000;

export const StationCourseTimeEdit: FC = () => {
  const { t } = useTranslation();
  const { station, courseDto, isFirstStation } =
    useContext(CourseStationContext);
  const { timeArrival } = station || {};
  const { timeFormat } = useFormats();
  const { updateEditingCourseMetadata } = useCourseDtoActions();
  const setCourseHasUnsavedStationChange = useSetRecoilState(
    courseHasUnsavedStationChangeAtom,
  );

  const initialValue = timeArrival ? timeArrival : undefined;
  const refValue = useRef(initialValue);

  const [timeValue, setTimeValue] = useState<Dayjs | null | undefined>(
    parseTimeAtReferenceDate({
      timeHHmm: initialValue,
      timezone: TimeZoneEnum.UTC,
    }),
  );
  useEffect(() => {
    if (initialValue !== refValue.current) {
      setTimeValue(
        parseTimeAtReferenceDate({
          timeHHmm: initialValue,
          timezone: TimeZoneEnum.UTC,
        }),
      );
      refValue.current = initialValue;
    }
  }, [initialValue]);

  const [error, setError] = useState<null | Error>(null);

  const handleTimeInputChange = useEvent((newValueUTC: Dayjs | null) => {
    setTimeValue(newValueUTC);

    const newTimeString = newValueUTC?.isValid()
      ? (newValueUTC.format(MAIN_TIME_FORMAT) as TTimeHHmm)
      : null;

    const hasChanged = newTimeString !== initialValue;

    if (hasChanged) {
      setCourseHasUnsavedStationChange(hasChanged);
      Log.debug(
        `Course time edit, time changed, new: ${newTimeString}, initial: ${initialValue}`,
      );
    }
  });

  useEffect(() => {
    if (error !== null) {
      setTimeout(() => {
        setError(null);
      }, UPDATED_STATION_TIME_VALIDATION_ERROR_LIFETIME);
    }
  }, [error]);

  const updateStationTimes = useEvent(async (updatedTimePart: TTimeHHmm) => {
    if (!courseDto) {
      Log.warnAndToast(
        t("errors.invalidEntityDTOSupplied", {
          entity: t(`course.${SINGLE_KEY}`),
        }),
      );
      return;
    }

    if (!station) {
      Log.warnAndToast(
        t("errors.invalidEntityDTOSupplied", {
          entity: t(`station.${SINGLE_KEY}`),
        }),
      );
      return;
    }

    if (updatedTimePart?.length !== TIME_STRING_LENGTH_HHmm) {
      Log.debug("Invalid time entered, no need to update other stations");
      return;
    }

    const metadataUpdated = updateCourseMetadataStationsTime(
      courseDto,
      station.id,
      updatedTimePart,
    );

    updateEditingCourseMetadata({
      metadata: metadataUpdated,
      skipApiCheck: true,
    });
  });

  const onBlur = useEvent(async () => {
    if (!timeValue?.isValid()) {
      Log.warnAndToast(t("errors.invalidTimeFormat"));
      return;
    }

    updateStationTimes(timeValue.format(MAIN_TIME_FORMAT) as TTimeHHmm);
  });

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== KeyboardKeys.ENTER) {
      return;
    }

    onBlur();
  };

  return (
    <StationTimeEditFieldStyled
      onChange={handleTimeInputChange}
      onBlur={onBlur}
      value={timeValue}
      timezone={TimeZoneEnum.UTC}
      format={timeFormat}
      onKeyDown={onKeyDown}
      disabled={isFirstStation}
      slotProps={{
        textField: {
          error: error !== null,
        },
      }}
    />
  );
};
